import React, { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, Circle, Polygon, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'leaflet-draw'
import { Box, Button, Typography, Alert } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

interface MapDrawingProps {
  address?: string
  location?: { lat: number; lng: number } | null | undefined
  defaultRadius?: number
  serviceArea?: any // GeoJSON
  backgroundArea?: any // GeoJSON - shown as reference layer (e.g., service area when drawing boost area)
  onServiceAreaChange: (serviceArea: any) => void
}

// Component to handle drawing controls
function DrawControl({
  location,
  defaultRadius,
  serviceArea,
  onServiceAreaChange,
}: {
  location: { lat: number; lng: number } | null | undefined
  defaultRadius: number
  serviceArea: any
  onServiceAreaChange: (serviceArea: any) => void
}) {
  const map = useMap()
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup())

  // Separate effect to manage layers based on serviceArea changes
  useEffect(() => {
    if (!map) return

    const drawnItems = drawnItemsRef.current

    // Clear all existing layers first
    drawnItems.clearLayers()

    // Load service area if present
    if (serviceArea) {
      try {
        const layer = L.geoJSON(serviceArea)
        layer.eachLayer((l) => {
          drawnItems.addLayer(l)
        })
      } catch (e) {
        console.error('Error loading service area:', e)
      }
    }
  }, [map, serviceArea]) // Re-run when serviceArea changes

  useEffect(() => {
    if (!map) return

    const drawnItems = drawnItemsRef.current
    map.addLayer(drawnItems)

    // Drawing controls
    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: '#2563eb',
            fillColor: '#3b82f6',
            fillOpacity: 0.3,
          },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    })

    map.addControl(drawControl)

    // When a shape is drawn
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer
      drawnItems.addLayer(layer) // Keep existing shapes - allow multiple

      // Convert ALL layers to FeatureCollection
      const allLayers = drawnItems.toGeoJSON()
      onServiceAreaChange(allLayers)
    })

    // When a shape is edited
    map.on(L.Draw.Event.EDITED, (event: any) => {
      // Convert ALL layers to FeatureCollection
      const allLayers = drawnItems.toGeoJSON()
      onServiceAreaChange(allLayers)
    })

    // When a shape is deleted
    map.on(L.Draw.Event.DELETED, () => {
      // Check if any layers remain
      if (drawnItems.getLayers().length === 0) {
        onServiceAreaChange(null) // All deleted
      } else {
        // Some remain - convert to FeatureCollection
        const allLayers = drawnItems.toGeoJSON()
        onServiceAreaChange(allLayers)
      }
    })

    return () => {
      map.removeControl(drawControl)
      map.off(L.Draw.Event.CREATED)
      map.off(L.Draw.Event.EDITED)
      map.off(L.Draw.Event.DELETED)
    }
  }, [map, onServiceAreaChange]) // Don't include serviceArea here - handled by separate effect

  // Center map on location when it changes
  useEffect(() => {
    if (location && map) {
      map.setView([location.lat, location.lng], 12)
    }
  }, [location, map])

  return null
}

export default function MapDrawing({
  address,
  location,
  defaultRadius = 30,
  serviceArea,
  backgroundArea,
  onServiceAreaChange,
}: MapDrawingProps) {
  const [showDefaultRadius, setShowDefaultRadius] = useState(!serviceArea)
  const [isDrawingMode, setIsDrawingMode] = useState(false)

  // Update showDefaultRadius when serviceArea changes
  useEffect(() => {
    setShowDefaultRadius(!serviceArea)
  }, [serviceArea])
  
  // Hide default radius when drawing
  useEffect(() => {
    if (isDrawingMode) {
      setShowDefaultRadius(false)
    } else if (!serviceArea) {
      setShowDefaultRadius(true)
    }
  }, [isDrawingMode, serviceArea])

  // Default center (Copenhagen, Denmark)
  const defaultCenter: [number, number] = [55.6761, 12.5683]
  const center: [number, number] = location ? [location.lat, location.lng] : defaultCenter

  const handleResetToDefault = () => {
    onServiceAreaChange(null)
    setShowDefaultRadius(true)
    setIsDrawingMode(false)
  }
  
  const handleStartDrawing = () => {
    // Clear any existing service area to start fresh
    onServiceAreaChange(null)
    setIsDrawingMode(true)
    setShowDefaultRadius(false)
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {serviceArea ? (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, bgcolor: 'success.main', borderRadius: '50%' }} />
              Brugerdefineret serviceområde tegnet ({Array.isArray(serviceArea?.features) ? serviceArea.features.length : 1} polygon{Array.isArray(serviceArea?.features) && serviceArea.features.length > 1 ? 'er' : ''})
            </Box>
          ) : (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RadioButtonUncheckedIcon fontSize="small" color="primary" />
              Standard radius: {defaultRadius} km
            </Box>
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {serviceArea ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleStartDrawing}
                color="primary"
              >
                Tegn mere
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleResetToDefault}
                color="error"
              >
                Nulstil til standard
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleStartDrawing}
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Tegn brugerdefineret område
            </Button>
          )}
          
          {!serviceArea && isDrawingMode && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetToDefault}
              color="secondary"
            >
              Annuller
            </Button>
          )}
        </Box>
      </Box>

      {!location && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Indtast en adresse i "Virksomhedsoplysninger" for at centrere kortet korrekt.
        </Alert>
      )}

      <Box sx={{ height: 500, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <MapContainer
          center={center}
          zoom={location ? 12 : 7}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Show background area (e.g., service area when drawing boost area) */}
          {backgroundArea && backgroundArea.type === 'FeatureCollection' && backgroundArea.features && (
            <>
              {backgroundArea.features.map((feature: any, index: number) => (
                <Polygon
                  key={`bg-${index}`}
                  positions={feature.geometry.coordinates[0].map((coord: [number, number]) => [coord[1], coord[0]])}
                  pathOptions={{
                    color: '#94a3b8',
                    fillColor: '#94a3b8',
                    fillOpacity: 0.15,
                    dashArray: '5, 10',
                    weight: 2,
                  }}
                />
              ))}
            </>
          )}
          
          {backgroundArea && backgroundArea.type === 'Feature' && backgroundArea.geometry && backgroundArea.geometry.coordinates && (
            <Polygon
              positions={backgroundArea.geometry.coordinates[0].map((coord: [number, number]) => [coord[1], coord[0]])}
              pathOptions={{
                color: '#94a3b8',
                fillColor: '#94a3b8',
                fillOpacity: 0.15,
                dashArray: '5, 10',
                weight: 2,
              }}
            />
          )}

          {/* Show default radius circle if no custom area and not in drawing mode */}
          {location && showDefaultRadius && !serviceArea && !isDrawingMode && (
            <Circle
              center={[location.lat, location.lng]}
              radius={defaultRadius * 1000} // Convert km to meters
              pathOptions={{
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.2,
              }}
            />
          )}

          {/* Show custom service area polygon(s) */}
          {serviceArea && serviceArea.type === 'FeatureCollection' && serviceArea.features && (
            <>
              {serviceArea.features.map((feature: any, index: number) => (
                <Polygon
                  key={index}
                  positions={feature.geometry.coordinates[0].map((coord: [number, number]) => [coord[1], coord[0]])}
                  pathOptions={{
                    color: '#2563eb',
                    fillColor: '#3b82f6',
                    fillOpacity: 0.3,
                  }}
                />
              ))}
            </>
          )}
          
          {/* Single polygon (backwards compatible) */}
          {serviceArea && serviceArea.type === 'Feature' && serviceArea.geometry && serviceArea.geometry.coordinates && (
            <Polygon
              positions={serviceArea.geometry.coordinates[0].map((coord: [number, number]) => [coord[1], coord[0]])}
              pathOptions={{
                color: '#2563eb',
                fillColor: '#3b82f6',
                fillOpacity: 0.3,
              }}
            />
          )}

          <DrawControl
            location={location}
            defaultRadius={defaultRadius}
            serviceArea={serviceArea}
            onServiceAreaChange={onServiceAreaChange}
          />
        </MapContainer>
      </Box>

      {isDrawingMode && !serviceArea && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            ✏️ Tegn dit serviceområde på kortet:
          </Typography>
          <Typography variant="body2" component="div">
            1. Klik på <strong>polygon-ikonet</strong> (🔷) i øverste højre hjørne af kortet
            <br />
            2. Klik på kortet for at placere hjørnepunkter
            <br />
            3. Dobbeltklik eller klik på første punkt for at afslutte polygonen
            <br />
            4. Du kan tegne flere polygoner for forskellige områder
            <br />
            <br />
            Klik <strong>"Annuller"</strong> for at gå tilbage til standard radius.
          </Typography>
        </Alert>
      )}
      
      {serviceArea && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>✓ Serviceområde defineret.</strong> Du kan tegne flere polygoner, redigere eksisterende eller nulstille til standard radius.
          </Typography>
        </Alert>
      )}
      
      {!serviceArea && !isDrawingMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Virksomheden dækker aktuelt <strong>{defaultRadius} km radius</strong> fra adressen. 
            Klik <strong>"Tegn brugerdefineret område"</strong> for at definere et præcist serviceområde.
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

